// Implements: F-USG-006, F-UIX-010
/**
 * The cost by version of one of the person's mockups, opened from its row in Mi consumo (mockup
 * v3 P7): the mockup's versions (the shared tree read, `versionTreeKey`), its eight newest, and
 * what each of those cost (one version-costs request for the eight).
 *
 * Owner 2026-10-19: beside each cost, what of it is the caller's own. Once the costs are known,
 * one cost-split request sends the runs on screen (the versions' and the failed turns') and reads
 * back which are the caller's, plus the mockup's totals, theirs and everybody's (`useCostSplit`,
 * keyed by person and mockup). The breakdown waits for both reads, so a total is never shown
 * without the caller's share beside it.
 *
 * Who sees it (P7-1): whoever may edit the mockup, read from the shared `GET /mockups/{id}`
 * (`useCostAccess`). A person who may now only read it (an editor demoted since the run), and a
 * `403` or `404` on the costs or on the split, get the sentence that says only editors see it: no
 * request, no error. Any other failure has its own error state with a retry of what failed, so a
 * failed breakdown never hides the list (F-UIX-010). The row mounts this keyed by mockup id
 * (checklist 14), and every read is keyed by it, so one mockup's answer never fills another's.
 */

import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { versionTreeKey } from "../../../shared/api/queryKeys";
import { isCostRefusal, useCostAccess, useVersionCosts } from "../../../shared/api/versionCosts";
import { fetchVersionNodes } from "../../../shared/api/versionTree";
import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import type { VersionTreeNode } from "../../../types/api";
import styles from "../components/MyUsage.module.css";
import { VersionCostBreakdown } from "../components/VersionCostBreakdown";
import { useCostSplit } from "../hooks/useCostSplit";
import { breakdownVersions } from "../model/versionBreakdown";

export interface VersionCostBreakdownContainerProps {
  /** The signed-in person, from the session: whose share the split reads. */
  readonly personId: string;
  readonly mockupId: string;
  readonly name: string;
}

export function VersionCostBreakdownContainer({
  personId,
  mockupId,
  name,
}: VersionCostBreakdownContainerProps): ReactElement {
  const { t } = useTranslation();
  const access = useCostAccess(mockupId);
  const mayEdit = access === "editor";
  const nodes = useQuery<readonly VersionTreeNode[], ApiError>({
    queryKey: versionTreeKey(mockupId),
    queryFn: () => fetchVersionNodes(mockupId),
    enabled: mayEdit,
  });
  const { shown, older } = breakdownVersions(nodes.data ?? []);
  const costs = useVersionCosts(
    mockupId,
    shown.map((node) => node.id),
    mayEdit && nodes.data !== undefined,
  );
  // The runs on screen, once their costs are known (none to ask for a mockup with no version).
  const costsKnown = nodes.data !== undefined && (shown.length === 0 || costs.costs !== undefined);
  const runIds = [
    ...shown.flatMap((node) => {
      const run = costs.costs?.get(node.id)?.run;
      return run ? [run.generation_id] : [];
    }),
    ...costs.failedRuns.map((run) => run.generation_id),
  ];
  const split = useCostSplit(personId, mockupId, runIds, mayEdit && costsKnown);
  const loading = <LoadingState label={t("cost.breakdown.title", { name })} />;

  if (
    access === "reader" ||
    access === "failed" ||
    isCostRefusal(nodes.error) ||
    isCostRefusal(costs.error) ||
    isCostRefusal(split.error)
  ) {
    return <p className={styles.hint}>{t("cost.breakdown.editorsOnly")}</p>;
  }
  if (access === "pending") return loading;
  if (nodes.isError) {
    return (
      <ErrorState
        headingLevel={3}
        error={nodes.error}
        onRetry={() => {
          void nodes.refetch();
        }}
      />
    );
  }
  if (nodes.data === undefined) return loading;
  if (shown.length > 0 && costs.error !== null) {
    return <ErrorState headingLevel={3} error={costs.error} onRetry={costs.refetch} />;
  }
  if (shown.length > 0 && costs.costs === undefined) return loading;
  if (split.isError) {
    return (
      <ErrorState
        headingLevel={3}
        error={split.error}
        onRetry={() => {
          void split.refetch();
        }}
      />
    );
  }
  if (split.data === undefined) return loading;
  return (
    <VersionCostBreakdown
      mockupId={mockupId}
      name={name}
      versions={shown}
      older={older}
      costs={costs.costs ?? new Map()}
      failedRuns={costs.failedRuns}
      ownRuns={new Set(split.data.own_generation_ids)}
      knownRuns={new Set(split.data.known_generation_ids)}
      totals={split.data.totals}
    />
  );
}
